import React, { useState, useEffect, useRef } from "react";
import { Fetch_All_Orders } from "../../../hooks/APIHooks";
import { Link } from "react-router-dom";
import { 
  FiPackage, 
  FiTruck, 
  FiCheckCircle, 
  FiClock, 
  FiDollarSign,
  FiFilter,
  FiSearch,
  FiMail,
  FiPhone,
  FiMapPin
} from "react-icons/fi";

function AllOrders() {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCustomer, setHoveredCustomer] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const customerRefs = useRef({});

  useEffect(() => {
    const fetchAllOrders = async () => {
      try {
        const API_KEY = process.env.REACT_APP_API_KEY;
        const response = await fetch(Fetch_All_Orders, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: API_KEY,
          },
        });
        const result = await response.json();

        if (result.status === "success") {
          // Sort orders by created_at in descending order (newest first)
          const sortedOrders = result.data.orders.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          );
          setOrders(sortedOrders);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("Failed to fetch orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllOrders();
  }, []);

  // Safe function to parse cart items
  const parseCartItems = (cartItems) => {
    try {
      if (!cartItems) return [];
      const parsed = JSON.parse(cartItems);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing cart items:', error);
      return [];
    }
  };

  // Calculate metrics
  const totalPurchases = orders.filter(
    (order) => order.razorpay_payment_id && order.razorpay_payment_id.startsWith("pay_")
  ).length;

  const today = new Date().toISOString().split("T")[0];
  const todayOrders = orders.filter((order) => order.created_at.startsWith(today)).length;

  const totalAmount = orders
    .reduce((acc, order) => {
      return order.razorpay_payment_id && order.razorpay_payment_id.startsWith("pay_")
        ? acc + parseFloat(order.total_amount)
        : acc;
    }, 0)
    .toFixed(2);

  const fulfilledOrders = orders.filter(order => order.delivery_status === "Delivered").length;
  const processingOrders = orders.filter(order => order.delivery_status === "Processing").length;

  // Filter and search orders
  const filteredOrders = orders.filter((order) => {
    // Status filter
    if (filter === "paid" && (!order.razorpay_payment_id || !order.razorpay_payment_id.startsWith("pay_"))) {
      return false;
    }
    if (filter === "unpaid" && (order.razorpay_payment_id && order.razorpay_payment_id.startsWith("pay_"))) {
      return false;
    }
    if (filter === "fulfilled" && order.delivery_status !== "Delivered") {
      return false;
    }
    if (filter === "processing" && order.delivery_status !== "Processing") {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        order.order_id.toString().includes(searchLower) ||
        (order.full_name && order.full_name.toLowerCase().includes(searchLower)) ||
        (order.email_address && order.email_address.toLowerCase().includes(searchLower)) ||
        (order.phone_number && order.phone_number.includes(searchTerm))
      );
    }

    return true;
  });

  // Sort filtered orders by date (newest first)
  const sortedFilteredOrders = [...filteredOrders].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  const getStatusBadge = (order) => {
    const isPaid = order.razorpay_payment_id && order.razorpay_payment_id.startsWith("pay_");
    const isFulfilled = order.delivery_status === "Delivered";
    
    if (!isPaid) {
      return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">Payment pending</span>;
    }
    if (isFulfilled) {
      return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Fulfilled</span>;
    }
    return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Processing</span>;
  };

  const getPaymentBadge = (order) => {
    if (order.razorpay_payment_id && order.razorpay_payment_id.startsWith("pay_")) {
      return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Paid</span>;
    }
    return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">Unpaid</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const orderDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - orderDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getTimeFromDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).toLowerCase();
  };

  const handleCustomerHover = (orderId, event) => {
    const element = customerRefs.current[orderId];
    if (element) {
      const rect = element.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX
      });
      setHoveredCustomer(orderId);
    }
  };

  // Customer info dropdown component - now positioned absolutely in the document
  const CustomerInfoDropdown = ({ order, isVisible, position }) => {
    if (!isVisible) return null;

    return (
      <div 
        className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4 min-w-64 max-w-80"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <FiMail className="text-gray-400 text-sm mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Email</p>
              <p className="text-sm text-gray-900 break-all">
                {order.email_address || 'No email provided'}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <FiPhone className="text-gray-400 text-sm mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-gray-500">Phone</p>
              <p className="text-sm text-gray-900">
                {order.phone_number || 'No phone provided'}
              </p>
            </div>
          </div>
          
          {order.shipping_address && (
            <div className="flex items-start gap-3 pt-2 border-t border-gray-100">
              <FiMapPin className="text-gray-400 text-sm mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-gray-500">Shipping Address</p>
                <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                  {order.shipping_address}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Arrow pointing to the customer name */}
        <div 
          className="absolute -top-2 left-4 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"
          style={{ left: '12px' }}
        ></div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
        <p className="text-gray-600 text-sm">Manage and track your orders</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Orders</p>
              <p className="text-[12px] font-bold">{orders.length}</p>
            </div>
            <FiPackage className="text-blue-500 text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Today</p>
              <p className="text-[12px] font-bold">{todayOrders}</p>
            </div>
            <FiClock className="text-green-500 text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Fulfilled</p>
              <p className="text-[12px] font-bold">{fulfilledOrders}</p>
            </div>
            <FiCheckCircle className="text-purple-500 text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Processing</p>
              <p className="text-[12px] font-bold">{processingOrders}</p>
            </div>
            <FiTruck className="text-orange-500 text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Revenue</p>
              <p className="text-[12px] font-bold">₹{totalAmount}</p>
            </div>
            <FiDollarSign className="text-green-500 text-lg" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
        <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-400 text-sm" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All orders</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="processing">Processing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedFilteredOrders.map((order) => {
                const cartItems = parseCartItems(order.cart_items);
                const itemCount = cartItems.length;
                
                return (
                  <tr 
                    key={order.order_id} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-2 py-2 whitespace-nowrap">
                      <Link 
                        to={`/auth/order-details/${order.order_id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium text-[12px]"
                      >
                        #{order.order_id}
                      </Link>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-gray-900 text-[12px]">{formatDate(order.created_at)} {getTimeFromDate(order.created_at)}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div 
                        ref={el => customerRefs.current[order.order_id] = el}
                        onMouseEnter={(e) => handleCustomerHover(order.order_id, e)}
                        onMouseLeave={() => setHoveredCustomer(null)}
                        className="text-[12px] font-medium text-gray-900 truncate max-w-[120px] cursor-help border-b border-dashed border-gray-300 hover:border-gray-500 transition-colors inline-block"
                      >
                        {order.full_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-[12px] font-medium text-gray-900">
                      ₹{order.total_amount || '0.00'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {getPaymentBadge(order)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {getStatusBadge(order)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-[12px] text-gray-600">
                      {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-[12px] text-gray-600">
                        {order.delivery_status || 'Pending'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedFilteredOrders.length === 0 && (
          <div className="text-center py-8">
            <FiPackage className="mx-auto text-gray-400 text-3xl mb-3" />
            <p className="text-gray-500 text-sm">No orders found</p>
            {searchTerm && (
              <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filter</p>
            )}
          </div>
        )}
      </div>

      {/* Global Customer Info Dropdown */}
      <CustomerInfoDropdown 
        order={sortedFilteredOrders.find(order => order.order_id === hoveredCustomer)} 
        isVisible={!!hoveredCustomer}
        position={dropdownPosition}
      />
 
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}

export default AllOrders;